import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Link, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { DialogDefaultV2 } from '../../../components/v2';
import { renderStatus } from '../../../components/v2/cell-renderers/status';
import { formatKg, formatKgMetric } from './constants';

const PESAGEM_PREVIEW_LIMIT = 3;

export const cellContentSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minWidth: 0,
  width: '100%',
  py: 0.5,
};

const nsirLinkSx = {
  color: 'info.main',
  fontWeight: 600,
  textDecoration: 'underline',
  '&:hover': { color: 'info.dark' },
};

const resolveMateriaisList = (row) => {
  if (Array.isArray(row.materiais) && row.materiais.length > 0) {
    return row.materiais
      .map((item) => ({
        key: item.id || item.tipo || item.label || item.tipoNome,
        label: item.label || item.tipoNome || item.tipo || '—',
        pesoKg: Number(item.pesoKg) || 0,
      }))
      .filter((item) => item.pesoKg > 0)
      .sort((a, b) => b.pesoKg - a.pesoKg);
  }

  const byMaterial = row.byMaterial;
  if (byMaterial && typeof byMaterial === 'object') {
    return Object.entries(byMaterial)
      .map(([tipo, pesoKg]) => ({
        key: tipo,
        label: row.materialLabels?.[tipo] || tipo,
        pesoKg: Number(pesoKg) || 0,
      }))
      .filter((item) => item.pesoKg > 0)
      .sort((a, b) => b.pesoKg - a.pesoKg);
  }

  return [];
};

const isDeclaracaoSemSucata = (row) => {
  if (row.semSucata === true || row.semSucata === 'true') return true;
  if (row.totalKg == null || row.totalKg === '') return false;
  if (Number(row.totalKg) !== 0) return false;
  return resolveMateriaisList(row).length === 0;
};

export const getUsoMutuoRowId = (row) => row?.nsmpId || row?.pesagemId || row?.id;

export const detailGridSx = {
  '& .MuiDataGrid-cell': {
    alignItems: 'center',
    lineHeight: 'normal',
    overflow: 'hidden',
    whiteSpace: 'normal',
  },
};

export const renderNsirCodigo = (row) => {
  const codigo = row.codigo;
  const loteId = row.nsirId || row.id;
  if (!codigo) return '—';
  if (!loteId) return codigo;
  const href = `${window.location.origin}/v2/nsir/${loteId}`;
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      sx={nsirLinkSx}
    >
      {codigo}
    </Link>
  );
};

export const renderNsirCol = (row) => (
  <Box sx={cellContentSx}>
    <Typography variant="body2" noWrap>
      <Box component="span" sx={{ fontWeight: 700 }}>Lote: </Box>
      {renderNsirCodigo(row)}
    </Typography>
    {row.malhaCodigo && (
      <Typography variant="body2" color="text.secondary" noWrap>
        Região: {row.malhaCodigo}
      </Typography>
    )}
  </Box>
);

export const renderStatusCol = (row) => (
  <Box sx={{ ...cellContentSx, alignItems: 'flex-start' }}>
    {row.status
      ? renderStatus({
          value: row.status,
          label: row.statusName || row.status,
        })
      : '—'}
  </Box>
);

export const renderPeriodoCol = (row) => {
  const label =
    row.periodoLabel
    || (row.periodoInicio && row.periodoFim
      ? `${moment(row.periodoInicio).format('DD/MM/YYYY')} – ${moment(row.periodoFim).format('DD/MM/YYYY')}`
      : '—');
  return (
    <Box sx={cellContentSx}>
      <Typography variant="body2">{label}</Typography>
    </Box>
  );
};

const materialLineKey = (item, index) => (
  item.key != null ? `${item.key}-${item.pesoKg}` : `${item.label}-${item.pesoKg}-${index}`
);

const MaterialLines = ({ materiais }) => (
  materiais.map((item, index) => (
    <Typography
      key={materialLineKey(item, index)}
      variant="caption"
      color="text.secondary"
      component="div"
      noWrap
      sx={{ lineHeight: 1.35 }}
    >
      · {item.label}: {formatKgMetric(item.pesoKg)} kg
    </Typography>
  ))
);

MaterialLines.propTypes = {
  materiais: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const PesagemCell = ({ row }) => {
  const [open, setOpen] = useState(false);
  const materiais = resolveMateriaisList(row);
  const totalLabel = formatKg(row.totalKg);
  const totalText = totalLabel === '—' ? '0,00' : totalLabel;
  const semSucata = isDeclaracaoSemSucata(row);
  const preview = materiais.slice(0, PESAGEM_PREVIEW_LIMIT);
  const hiddenCount = materiais.length - preview.length;

  const closeDialog = () => setOpen(false);

  return (
    <Box sx={cellContentSx}>
      <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
        Total: {totalText} kg
      </Typography>
      {semSucata ? (
        <Chip
          label="Sem sucata"
          size="small"
          variant="outlined"
          sx={{ mt: 0.5, alignSelf: 'flex-start', height: 22 }}
        />
      ) : preview.length > 0 ? (
        <>
          <MaterialLines materiais={preview} />
          {hiddenCount > 0 && (
            <Link
              component="button"
              type="button"
              variant="caption"
              onClick={(event) => {
                event.stopPropagation();
                setOpen(true);
              }}
              sx={{
                ...nsirLinkSx,
                alignSelf: 'flex-start',
                fontSize: '0.75rem',
                lineHeight: 1.35,
                mt: 0.25,
              }}
            >
              Visualizar mais
            </Link>
          )}
        </>
      ) : (
        row.quantidadeMateriais != null && row.quantidadeMateriais > 0 && (
          <Typography variant="caption" color="text.secondary">
            {row.quantidadeMateriais} material(is)
          </Typography>
        )
      )}
      <DialogDefaultV2
        open={open}
        handlerCancel={closeDialog}
        handlerConfirm={closeDialog}
        title={row.codigo || 'Pesagem'}
        readOnly
        maxWidth="xs"
        fullWidth
      >
        <Stack spacing={1.5}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Total: {totalText} kg
          </Typography>
          {materiais.length > 0 ? (
            <Stack spacing={0.5}>
              {materiais.map((item, index) => (
                <Stack
                  key={materialLineKey(item, index)}
                  direction="row"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Typography variant="body2" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {formatKgMetric(item.pesoKg)} kg
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nenhum material informado.
            </Typography>
          )}
        </Stack>
      </DialogDefaultV2>
    </Box>
  );
};

PesagemCell.propTypes = {
  row: PropTypes.object.isRequired,
};

export const renderPesoCol = (row) => <PesagemCell row={row} />;
